import React, { useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import SimpleFormDesigner from './admin/SimpleFormDesigner';
import FormRenderer from './FormRenderer';
import { Form, getFormById } from '../services/formService';

/**
 * Page that demonstrates the form designer and renderer working together
 */
const FormPreviewPage: React.FC = () => {
  const { user } = useAuth();
  const [previewMode, setPreviewMode] = useState(false);
  const [formToPreview, setFormToPreview] = useState<Form | null>(null);
  const [previewFormId, setPreviewFormId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [submitResult, setSubmitResult] = useState<Record<string, any> | null>(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  
  // Handle form design save - transition to preview mode
  const handleFormSaved = async (formId: string) => {
    setIsLoading(true);
    setPreviewFormId(formId);
    
    try {
      const form = await getFormById(formId);
      if (form) {
        setFormToPreview(form);
        setPreviewMode(true);
      }
    } catch (error) {
      console.error('Error loading form for preview:', error);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Handle form submission in preview mode
  const handleFormSubmit = (formData: Record<string, any>) => {
    setIsLoading(true);
    
    // Simulate API request
    setTimeout(() => {
      setSubmitResult(formData);
      setSubmitSuccess(true);
      setIsLoading(false);
    }, 1500);
  };
  
  // Return to design mode
  const handleBackToDesign = () => {
    setPreviewMode(false);
    setSubmitResult(null);
    setSubmitSuccess(false);
  };
  
  // Reset the preview
  const handleResetPreview = () => {
    setSubmitResult(null);
    setSubmitSuccess(false);
  };
  
  if (!user) {
    return (
      <div className="text-center py-10">
        <p>Please log in to access this page.</p>
      </div>
    );
  }
  
  return (
    <div className="max-w-6xl mx-auto p-4">
      <div className="mb-6 flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">
          {previewMode ? 'Form Preview' : 'Form Designer'}
        </h1>
        
        <div>
          {previewMode && (
            <button
              onClick={handleBackToDesign}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 mr-2"
            >
              Back to Designer
            </button>
          )}
          
          {!previewMode && (
            <div className="text-sm text-gray-500">
              Save your form to preview it
            </div>
          )}
          
          {previewMode && submitSuccess && (
            <button
              onClick={handleResetPreview}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              Test Again
            </button>
          )}
        </div>
      </div>
      
      {isLoading && (
        <div className="fixed inset-0 bg-gray-800 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg shadow-xl">
            <div className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-lg font-medium">Loading...</span>
            </div>
          </div>
        </div>
      )}
      
      {!previewMode && (
        <div className="mb-4 p-4 bg-blue-50 rounded-md text-blue-700 text-sm">
          <p className="font-medium">Form Designer Demo</p>
          <p>Design your form and save it to see the preview. Then test the form functionality in the preview mode.</p>
        </div>
      )}
      
      {previewMode && !submitSuccess && formToPreview && (
        <div className="mb-4 p-4 bg-green-50 rounded-md text-green-700 text-sm">
          <p className="font-medium">Form Preview Mode</p>
          <p>This is how your form will appear to users. Try filling it out and submitting to test the functionality.</p>
        </div>
      )}
      
      {previewMode && submitSuccess && (
        <div className="mb-6">
          <div className="bg-green-50 border border-green-200 rounded-md p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Form Submitted Successfully</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>In a real application, this data would be sent to the server.</p>
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-white shadow overflow-hidden rounded-lg">
            <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
              <h3 className="text-lg font-medium text-gray-900">Form Submission Data</h3>
              <p className="mt-1 text-sm text-gray-500">This data would be sent to the server or used in AI prompts.</p>
            </div>
            <div className="px-4 py-5 sm:p-6">
              <pre className="bg-gray-50 p-4 rounded-md overflow-auto max-h-96 text-sm">
                {JSON.stringify(submitResult, null, 2)}
              </pre>
            </div>
          </div>
        </div>
      )}
      
      {!previewMode ? (
        <SimpleFormDesigner onFormSave={handleFormSaved} />
      ) : (
        formToPreview && !submitSuccess && (
          <FormRenderer 
            form={formToPreview} 
            onSubmit={handleFormSubmit} 
            isSubmitting={isLoading} 
          />
        )
      )}
    </div>
  );
};

export default FormPreviewPage; 